import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { CRM_DROPDOWN_MAX_OPTIONS } from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InputGroup } from '../../../components/InputGroup';
import { ToggleButton } from '../../../components/ToggleButton';
import { ToggleButtonGroup } from '../../../components/ToggleButtonGroup';
import { Toggles } from '../../../components/ToggleButtonGroup/ToggleButtonGroup';
import { DropdownOption } from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import {
    activeFunctionsOptions,
    tempAdjuvants,
    tempEmulsifiers,
    tempLiquidColorant,
    tempSolvents,
    tempStabiliers,
} from './activeFunctionOptions';

export const StepActiveFunction: FC<StepProps> = ({ onValidate = noOp }) => {
  const { t } = useTranslation();
  const [
    {
      activeFunctions,
      functionAdjuvant,
      functionEmulsifier,
      functionLiquidColorant,
      functionSolvent,
      functionStabilizer,
    },
    {
      setActiveFunctions,
      setFunctionAdjuvant,
      setFunctionEmulisifer,
      setFunctionLiquidColorant,
      setFunctionSolvent,
      setFunctionStabilizer,
    },
  ] = useBriefingState();
  const dispatch = useDispatch();

  const allFunctionOptions = useMemo(() => [
    tempAdjuvants,
    tempEmulsifiers,
    tempLiquidColorant,
    tempSolvents,
    tempStabiliers,
  ], []);

  const [isActiveFunctionValid] = useState(true);
  const isStepValid = useMemo(
    () => isActiveFunctionValid,
    [isActiveFunctionValid],
  );

  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const [initialFunctionToggles] = useState(activeFunctions as Toggles);

  const onChangeFunctionToggles = useCallback(
    (toggles: Toggles) => dispatch(setActiveFunctions(toggles as string[])),
    [dispatch, setActiveFunctions],
  );

  const onChangeFunctionDropdown = useCallback(
    (selection: DropdownOption[], functionName: string) => {
      switch (functionName) {
        case 'emulsifier':
          dispatch(setFunctionEmulisifer(selection));
          break;

        case 'stabilizer':
          dispatch(setFunctionStabilizer(selection));
          break;

        case 'liquidColorant':
          dispatch(setFunctionLiquidColorant(selection));
          break;

        case 'solvent':
          dispatch(setFunctionSolvent(selection));
          break;

        case 'adjuvant':
          dispatch(setFunctionAdjuvant(selection));
          break;
      }
    },
    [
      dispatch,
      setFunctionAdjuvant,
      setFunctionEmulisifer,
      setFunctionLiquidColorant,
      setFunctionSolvent,
      setFunctionStabilizer,
    ],
  );

  const getInitialDropdownSelection = useCallback(
    (name: string): DropdownOption[] | null => {
      switch (name) {
        case 'emulsifier':
          return functionEmulsifier;

        case 'stabilizer':
          return functionStabilizer;

        case 'liquidColorant':
          return functionLiquidColorant;

        case 'solvent':
          return functionSolvent;

        case 'adjuvant':
          return functionAdjuvant;

        default:
          return null;
      }
    },
    [
      functionAdjuvant,
      functionEmulsifier,
      functionLiquidColorant,
      functionSolvent,
      functionStabilizer,
    ],
  );

  const renderFunctionDropdown = useCallback(
    (functionName: string) => {
      const functionOptions = allFunctionOptions
        .filter((optionSet) => optionSet[0].function === functionName)
        .map((option) =>
          option.map((func) => ({ name: func.name, value: func.value })),
        )
        .flat();
      const initialDropdownSelection =
        getInitialDropdownSelection(functionName);

      return (
        <div className="w-full md:w-6/12">
          <div className="space-y-1" data-testid={'active-function-dropdown'}>
            <Heading
              level="h3"
              text={t(`topics:active-function:${functionName}:name`)}
              margin={0}
            />
            <InputGroup
              label={t(`topics:active-function:${functionName}:choose`)}
              optional={true}
            >
              <Dropdown
                initialSelection={initialDropdownSelection || []}
                options={functionOptions}
                error={!functionOptions}
                multiselect={true}
                maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                onChange={(selected) =>
                  onChangeFunctionDropdown(
                    selected as DropdownOption[],
                    functionName,
                  )
                }
              />
              <p className="mt-1 text-gray-600 text-2xs font-regular">
                {t('labels:multiSelectHint')}
              </p>
            </InputGroup>
          </div>
        </div>
      );
    },
    [
      t,
      allFunctionOptions,
      onChangeFunctionDropdown,
      getInitialDropdownSelection,
    ],
  );

  const renderToggleButtons = useMemo(() => {
    return (
      <ToggleButtonGroup
        initialToggles={initialFunctionToggles}
        multi={true}
        onChange={onChangeFunctionToggles}
      >
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {activeFunctionsOptions.map(({ name, value }) => (
            <ToggleButton
              text={t(`${name}:name`)}
              value={value}
              key={value}
              on={true}
              dataTest={'active-function-application-button'}
            />
          ))}
        </div>
      </ToggleButtonGroup>
    );
  }, [t, onChangeFunctionToggles, initialFunctionToggles]);

  return (
    <TemplateFlexGridContainer.BriefingStep xsSpaceY={1} mdSpaceY={1}>
      <Heading
        level="h1"
        text={t('topics:active-function:heading:general')}
        margin={0}
        optionalField={true}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <p>{t('labels:multiSelectHint')}</p>
        <div className="w-full md:w-9/12">
          <TemplateFlexGridContainer.BriefingStepSection mdSpaceY={8}>
            {renderToggleButtons}
            {activeFunctions &&
              activeFunctions.map((selectedFunction) =>
                renderFunctionDropdown(selectedFunction),
              )}
          </TemplateFlexGridContainer.BriefingStepSection>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
